import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

export const BRAND_SUBTITLE = 'COMMERCIAL PERMIT INTELLIGENCE';
export const BRAND_TAGLINE = 'TractTrace | Commercial Permit & Lead Intelligence Engine';
export const LEGAL_FOOTER = '© 2026 Junoware, LLC. All rights reserved.';
export const INTEGRATIONS_DISCLAIMER =
  'All product names, logos, and brands are property of their respective owners and are used for compatibility identification purposes only.';
export const PRIMARY_CTA_LABEL = 'Get there first';
export const PRIMARY_CTA_PATH = '/audit-request';
/** Typical commercial jobsite package for a rental yard, materials desk, or trade buy. */
export const FOMO_MISS_MINUTES = 15;
export const FOMO_JOBSITE_USD = 150_000;
export const CORE_OFFER = `Fifteen minutes late can cost you a $150,000 jobsite.`;
export const ENTERPRISE_DEMO_CTA_LABEL = 'Schedule Enterprise Demo';
export const ENTERPRISE_DEMO_PATH = '/demo';
export const CANONICAL_ORIGIN = 'https://tracttrace.com';
export const BRAND_LOGO_SRC = '/logo.png';

const logoSizeClass = {
  sm: 'h-8 w-8',
  md: 'h-9 w-9',
  lg: 'h-11 w-11',
} as const;

/**
 * Glow must use drop-shadow (not box-shadow) so it follows the hexagon alpha,
 * not the rectangular PNG bounds. logo.png bg is transparent.
 */
const brandLogoGlowClass =
  'object-contain drop-shadow-[0_0_10px_rgba(34,211,238,0.55)] transition-[filter] duration-200';

export function BrandMark({
  to = '/',
  showSubtitle = true,
  size = 'md',
}: {
  to?: string;
  showSubtitle?: boolean;
  size?: 'sm' | 'md' | 'lg';
}) {
  const titleClass = size === 'lg' ? 'text-2xl' : size === 'sm' ? 'text-lg' : 'text-xl';

  return (
    <Link
      to={to}
      className="group flex min-w-0 max-w-full cursor-pointer items-center justify-start gap-2.5 text-left md:gap-3"
    >
      <img
        src={BRAND_LOGO_SRC}
        alt=""
        width={44}
        height={44}
        decoding="async"
        className={`${logoSizeClass[size]} ${brandLogoGlowClass} shrink-0 group-hover:drop-shadow-[0_0_16px_rgba(34,211,238,0.75)]`}
      />
      <span className="min-w-0 overflow-hidden">
        <p className={`brand-mark ${titleClass}`}>
          Tract<span className="brand-mark-accent">Trace</span>
        </p>
        {showSubtitle ? (
          <p className="mt-0.5 truncate font-mono text-[9px] font-medium uppercase tracking-[0.14em] text-cyan-300 transition group-hover:opacity-90 sm:tracking-[0.18em] md:mt-1 md:text-[10px] md:tracking-[0.2em]">
            {BRAND_SUBTITLE}
          </p>
        ) : null}
      </span>
    </Link>
  );
}

export function PageShell({ children }: { children: ReactNode }) {
  return <div className="min-h-full bg-[#0B0F17] text-slate-100">{children}</div>;
}

export function SectionRule() {
  return <div className="exec-line my-1 w-full" aria-hidden />;
}

const footerLinkClass =
  'cursor-pointer font-sans text-sm text-slate-400 transition-colors duration-200 hover:text-cyan-300';

const FOOTER_COLUMNS = [
  {
    title: 'Product',
    links: [
      { to: '/#how-it-works', label: 'How it works', hash: true },
      { to: '/#layers', label: 'The 3 Layers', hash: true },
      { to: '/#pricing', label: 'Pricing', hash: true },
      { to: '/audit-request', label: 'Get there first' },
      { to: '/#sample-filing', label: 'Sample filing', hash: true },
    ],
  },
  {
    title: 'Coverage',
    links: [
      { to: '/audit-request', label: 'Request a metro' },
      { to: '/contact', label: 'Talk to a specialist' },
    ],
  },
  {
    title: 'Company',
    links: [
      { to: '/', label: 'Home' },
      { to: '/terms', label: 'Terms of Use' },
      { to: '/privacy', label: 'Privacy Policy' },
      { to: '/contact', label: 'Contact' },
    ],
  },
] as const;

function FooterNavLink({
  to,
  label,
  hash,
  external,
}: {
  to: string;
  label: string;
  hash?: boolean;
  external?: boolean;
}) {
  if (external || hash) {
    const isHttp = to.startsWith('http://') || to.startsWith('https://');
    return (
      <a
        href={to}
        className={footerLinkClass}
        {...(isHttp ? { target: '_blank', rel: 'noreferrer' } : {})}
      >
        {label}
      </a>
    );
  }

  return (
    <Link to={to} className={footerLinkClass}>
      {label}
    </Link>
  );
}

/** Full-width institutional site footer used across marketing and console pages. */
export function SiteFooter() {
  return (
    <footer className="mt-16 border-t border-slate-800/80 bg-[#0B0F17]">
      <div className="mx-auto max-w-7xl px-4 py-12 md:py-16">
        <div className="grid grid-cols-1 gap-10 text-center md:grid-cols-12 md:gap-8 md:text-left">
          <div className="flex flex-col items-center md:col-span-5 md:items-start">
            <Link
              to="/"
              className="group flex items-center gap-3 transition-opacity hover:opacity-90"
            >
              <img
                src={BRAND_LOGO_SRC}
                alt=""
                width={40}
                height={40}
                decoding="async"
                className={`h-10 w-10 ${brandLogoGlowClass} shrink-0`}
              />
              <span className="brand-mark text-xl">
                Tract<span className="brand-mark-accent">Trace</span>
              </span>
            </Link>
            <p className="mt-4 max-w-sm font-sans text-sm leading-relaxed text-slate-400">
              Commercial permit coverage for equipment rental fleets, material suppliers, trade
              subcontractors, and commercial insurance brokers. Tell us your metro. A specialist
              will call you.
            </p>
            <p className="mt-3 font-mono text-[10px] font-medium uppercase tracking-[0.2em] text-cyan-300">
              {BRAND_SUBTITLE}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-3 md:col-span-7">
            {FOOTER_COLUMNS.map((column) => (
              <div key={column.title}>
                <p className="font-mono text-[10px] font-medium uppercase tracking-[0.18em] text-slate-500">
                  {column.title}
                </p>
                <ul className="mt-4 space-y-3">
                  {column.links.map((link) => (
                    <li key={link.label}>
                      <FooterNavLink {...link} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-slate-800/80">
        <div className="mx-auto max-w-7xl space-y-2 px-4 py-5 text-center font-mono text-xs text-slate-500">
          <p>{INTEGRATIONS_DISCLAIMER}</p>
          <p>{LEGAL_FOOTER}</p>
        </div>
      </div>
    </footer>
  );
}

/** @deprecated Prefer SiteFooter - retained so existing imports keep rendering the full footer. */
export function LegalFooter(_props?: {
  links?: ReadonlyArray<{ to: string; label: string; hash?: boolean }>;
}) {
  return <SiteFooter />;
}
